import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { ProgressCircle } from "../components/home/ProgressCircle";
import { QuestionChips } from "../components/questionnaire/QuestionChips";
import { QuestionRadio } from "../components/questionnaire/QuestionRadio";
import { QuestionSection } from "../components/questionnaire/QuestionSection";
import { QuestionText } from "../components/questionnaire/QuestionText";
import { QuestionUpload } from "../components/questionnaire/QuestionUpload";
import { FullPageErrorState } from "../components/ui/FullPageErrorState";
import { QUESTIONS } from "../config/questionnaire";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import {
	getQuestionnaireAnswers,
	saveQuestionnaireAnswers,
} from "../services/questionnaireService";
import type {
	QuestionConfig,
	SaveQuestionnaireRequest,
} from "../types/questionnaire";
import styles from "./QuestionnairePage.module.css";

function LoadingSkeleton() {
	return (
		<div className={styles.skeletonContainer} aria-busy="true">
			<div className={styles.skeletonBlock} />
			<div className={styles.skeletonBlock} />
			<div className={styles.skeletonBlock} />
			<div className={styles.skeletonBlock} />
		</div>
	);
}

export function QuestionnairePage() {
	const { t, direction } = useClientTranslations("questionnaire");
	const navigate = useNavigate();
	const { logEvent } = useAnalytics();
	const queryClient = useQueryClient();

	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (!hasTrackedPageView.current) {
			hasTrackedPageView.current = true;
			logEvent("questionnaire_page_viewed");
		}
	}, [logEvent]);

	const { data, isLoading, isError, refetch } = useQuery({
		queryKey: ["questionnaire-answers"],
		queryFn: getQuestionnaireAnswers,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: true,
	});

	const [answers, setAnswers] = useState<Record<string, unknown>>({});

	useEffect(() => {
		if (data?.answers) {
			setAnswers(data.answers);
		}
	}, [data]);

	function handleChange(questionId: string, value: unknown) {
		setAnswers((prev) => ({ ...prev, [questionId]: value }));
	}

	const saveMutation = useMutation({
		mutationFn: (req: SaveQuestionnaireRequest) =>
			saveQuestionnaireAnswers(req),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["questionnaire-answers"],
			});
			queryClient.invalidateQueries({
				queryKey: ["tasks"],
			});
			toast(t("saveSuccess"));
			logEvent("questionnaire_submitted");
		},
		onError: () => {
			toast.error(t("error.title"));
		},
	});

	function handleApprove() {
		saveMutation.mutate({
			answers,
			schemaVersion: data?.schemaVersion ?? 1,
		});
	}

	function renderQuestion(question: QuestionConfig) {
		switch (question.type) {
			case "text":
				return (
					<QuestionText
						value={(answers[question.id] as string) || ""}
						onChange={(v) => handleChange(question.id, v)}
						placeholder={t(`${question.i18nKey}.placeholder`)}
					/>
				);
			case "multiselect":
				return (
					<QuestionChips
						options={question.options || []}
						value={(answers[question.id] as string[]) || []}
						onChange={(v) => handleChange(question.id, v)}
						noneOptionKey={
							question.options?.[question.options.length - 1] || ""
						}
						additionalTextValue={
							(answers[`${question.id}_other`] as string) || ""
						}
						onAdditionalTextChange={(v) =>
							handleChange(`${question.id}_other`, v)
						}
						additionalTextPlaceholder={t(`${question.i18nKey}.placeholder`)}
						specifyLabel={t(`${question.i18nKey}.specifyLabel`)}
						t={t}
					/>
				);
			case "radio":
				return (
					<QuestionRadio
						options={question.options || []}
						value={(answers[question.id] as string) || null}
						onChange={(v) => handleChange(question.id, v)}
						t={t}
						questionId={question.id}
					/>
				);
			case "file_upload":
				return (
					<QuestionUpload
						documentIds={(answers[question.id] as string[]) || []}
						onUploadComplete={(docId) =>
							handleChange(question.id, [
								...((answers[question.id] as string[]) || []),
								docId,
							])
						}
						onRemoveDocument={(docId) =>
							handleChange(
								question.id,
								((answers[question.id] as string[]) || []).filter(
									(id: string) => id !== docId,
								),
							)
						}
						onUploadSuccess={() => logEvent("questionnaire_document_uploaded")}
						t={t}
					/>
				);
		}
	}

	return (
		<div className={styles.page} dir={direction}>
			<Header />
			{isLoading ? (
				<LoadingSkeleton />
			) : isError ? (
				<FullPageErrorState
					titleKey="error.title"
					descriptionKey="error.description"
					onRetry={() => refetch()}
					t={t}
				/>
			) : (
				<>
					<div className={styles.backRow}>
						<button
							type="button"
							className={styles.backButton}
							onClick={() => navigate("/get-ready")}
							aria-label="Back"
						>
							<ArrowLeftIcon className={styles.backIcon} />
						</button>
						<h1 className={styles.backTitle}>{t("pageTitle")}</h1>
					</div>

					<div className={styles.statusSection}>
						<ProgressCircle percentage={data?.progress ?? 0} />
						<div className={styles.statusText}>
							<h2 className={styles.statusTitle}>{t("title")}</h2>
							<p className={styles.hipaaNote}>{t("subtitle")}</p>
						</div>
					</div>

					<div className={styles.questionsContainer}>
						{QUESTIONS.map((question) => (
							<QuestionSection
								key={question.id}
								title={t(`${question.i18nKey}.label`)}
								subtitle={t(`${question.i18nKey}.help`)}
								helpText={t(`${question.i18nKey}.help`)}
							>
								{renderQuestion(question)}
							</QuestionSection>
						))}
					</div>

					<button
						type="button"
						className={styles.approveButton}
						onClick={handleApprove}
						disabled={saveMutation.isPending}
					>
						{saveMutation.isPending ? (
							<span className={styles.spinner} />
						) : (
							t("approve")
						)}
					</button>
				</>
			)}
		</div>
	);
}
